import { Injectable, NgZone } from '@angular/core';
import { Observable } from 'rxjs';
import { Zutrittspunkt } from '../models/zutrittspunkt';
import { HttpClient } from '@angular/common/http';
import { KeycloakService } from 'src/shared/services/keycloak.service';
import { environment } from 'src/environments/environment';

const ctrl = new AbortController();
@Injectable({
    providedIn: 'root',
})
export class BildverifizierungService {
    private zutrittspunkt1AbortController!: AbortController;
    private zutrittspunkt2AbortController!: AbortController;
    private istZutrittspunkt1Getrennt: boolean = false;
    private istZutrittspunkt2Getrennt: boolean = false;
    private url: string = environment.sse_environment;
    constructor(
        private http: HttpClient,
        private zone: NgZone,
        private keycloakService: KeycloakService,
    ) {
        if (
            !window.location.hostname.includes('staging') &&
            !window.location.hostname.includes('development')
        ) {
            this.url = this.url.replace('-staging', '');
        }
    }

    public getServerSentEventZutrittspunkt1(
        projektId: string,
        leseeinheitId: string,
    ): Observable<any> {
        return new Observable<any>((observer) => {
            this.zutrittspunkt1AbortController = new AbortController();
            const signal = this.zutrittspunkt1AbortController?.signal;
            let retryDelay = 3000;
            let buffer = '';

            const connectZutrittspunkt1 = async () => {
                await this.keycloakService.updateToken(60);

                try {
                    const res = await fetch(
                        `${this.url}/api/zutrittskontrolle/sse/verbinden/?ProjektId=${projektId}&LeseeinheitId=${leseeinheitId}`,
                        {
                            credentials: 'include', // entspricht withCredentials: true
                            headers: {
                                Authorization: `${this.keycloakService.getToken()}`,
                            },
                            signal,
                        },
                    );
                    if (!res.ok) throw new Error(`HTTP ${res.status}`);

                    const reader = res
                        .body!.pipeThrough(new TextDecoderStream())
                        .getReader();

                    while (true) {
                        const { value, done } = await reader.read();
                        if (done) break;
                        buffer += value;
                        const parts = buffer.split('\n\n');
                        for (let i = 0; i < parts.length - 1; i++) {
                            const eventData = this.parseSse(parts[i]);
                            this.zone.run(() => observer.next(eventData));
                        }
                        buffer = parts[parts.length - 1];
                    }
                } catch (err) {
                    if (signal.aborted) {
                        if (!this.istZutrittspunkt1Getrennt) {
                            setTimeout(connectZutrittspunkt1, retryDelay);
                        }
                        console.log(
                            'getrennt',
                            this.istZutrittspunkt1Getrennt,
                            Date.now(),
                        );
                        // Gewolltes Schließen
                    } else {
                        // automatischer Reconnect
                        console.log('reconnect', Date.now());
                        setTimeout(connectZutrittspunkt1, retryDelay);
                    }
                }
            };

            // Starte die Verbindung
            connectZutrittspunkt1();

            // Teardown-Logik: wird aufgerufen, wenn subscriber unsubscribet
            return () => {
                if (this.zutrittspunkt1AbortController) {
                    this.zutrittspunkt1AbortController?.abort();
                }
            };
        });
    }

    private parseSse(raw: string): string {
        for (const line of raw?.split('\n')) {
            if (line?.startsWith('data:')) {
                return line.slice(5).trim();
            }
        }
        return '';
    }

    public getServerSentEventZutrittspunkt2(
        projektId: string,
        leseeinheitId: string,
    ): Observable<any> {
        return new Observable<any>((observer) => {
            this.zutrittspunkt2AbortController = new AbortController();
            const signal = this.zutrittspunkt2AbortController?.signal;
            let retryDelay = 3000;
            let buffer = '';

            const connectZutrittspunkt2 = async () => {
                await this.keycloakService.updateToken(60);
                try {
                    const res = await fetch(
                        `${this.url}/api/zutrittskontrolle/sse/verbinden/?ProjektId=${projektId}&LeseeinheitId=${leseeinheitId}`,
                        {
                            credentials: 'include', // entspricht withCredentials: true
                            headers: {
                                Authorization: `${this.keycloakService.getToken()}`,
                            },
                            signal,
                        },
                    );
                    if (!res.ok) throw new Error(`HTTP ${res.status}`);

                    const reader = res
                        .body!.pipeThrough(new TextDecoderStream())
                        .getReader();

                    while (true) {
                        const { value, done } = await reader.read();
                        if (done) break;
                        buffer += value;
                        const parts = buffer.split('\n\n');
                        for (let i = 0; i < parts.length - 1; i++) {
                            const eventData = this.parseSse(parts[i]);
                            this.zone.run(() => observer.next(eventData));
                        }
                        buffer = parts[parts.length - 1];
                    }
                } catch (err) {
                    if (signal.aborted) {
                        if (!this.istZutrittspunkt2Getrennt) {
                            setTimeout(connectZutrittspunkt2, retryDelay);
                        }
                        console.log('getrennt', Date.now());
                        // Gewolltes Schließen
                    } else {
                        // automatischer Reconnect
                        console.log('reconnect', Date.now());
                        setTimeout(connectZutrittspunkt2, retryDelay);
                    }
                }
            };

            // Starte die Verbindung
            connectZutrittspunkt2();

            // Teardown-Logik: wird aufgerufen, wenn subscriber unsubscribet
            return () => {
                if (this.istZutrittspunkt2Getrennt) {
                    this.zutrittspunkt2AbortController?.abort();
                }
            };
        });
    }

    public getZutrittspunkte(projektId: string): Observable<Zutrittspunkt[]> {
        return this.http.get<Zutrittspunkt[]>(
            this.url +
                `/api/zutrittskontrolle/bildverifizierung/de-DE/${projektId}/leseeinheiten`,
        );
    }

    public closeZutrittspunkt1(): void {
        if (this.zutrittspunkt1AbortController) {
            this.istZutrittspunkt1Getrennt = true;
            this.zutrittspunkt1AbortController.abort();
        }
    }

    public closeZutrittspunkt2(): void {
        if (this.zutrittspunkt2AbortController) {
            this.istZutrittspunkt2Getrennt = true;
            this.zutrittspunkt2AbortController.abort();
        }
    }
}
